import http from '@/utils/http';
import { IAM_ENDPOINTS } from '@/config/api';

/**
 * Account-related API calls
 */
export const accountApi = {
  /**
   * Creates a new account.
   * @param {object} accountData - The account data, including organization and administrator.
   * @returns {Promise}
   */
  createAccount(accountData) {
    return http.post('/iam/tenant-api/v1/accounts', accountData, {
      headers: {
        'X-Tenant-ID-Debug': 'lzhtest'
      }
    });
  },

  /**
   * Finds accounts.
   * @param {object} params - Query parameters.
   * @returns {Promise}
   */
  findAccounts(params) {
    return http.get('/iam/tenant-api/v1/accounts', { params });
  },

  /**
   * Deletes a specific account by ID.
   * @param {string} id - The account ID.
   * @returns {Promise}
   */
  deleteAccount(id) {
    return http.delete(`/iam/tenant-api/v1/accounts/${id}`);
  },

  /**
   * Gets a specific account by ID.
   * @param {string} id - The account ID.
   * @returns {Promise}
   */
  getAccount(id) {
    return http.get(`/iam/tenant-api/v1/accounts/${id}`);
  },

  /**
   * Updates organization information for an account.
   * @param {string} id - The account ID.
   * @param {object} orgInfo - The new organization information.
   * @returns {Promise}
   */
  updateOrganizationInfo(id, orgInfo) {
    return http.put(`/iam/tenant-api/v1/accounts/${id}/organization-info`, orgInfo);
  },

  /**
   * Clears organization information for an account.
   * @param {string} id - The account ID.
   * @returns {Promise}
   */
  clearOrganizationInfo(id) {
    return http.delete(`/iam/tenant-api/v1/accounts/${id}/organization-info`);
  },

  /**
   * Transfers administrator role.
   * @param {string} id - The account ID.
   * @param {object} adminData - Data for the new administrator.
   * @returns {Promise}
   */
  transferAdministrator(id, adminData) {
    return http.put(`/iam/tenant-api/v1/accounts/${id}/administrator`, adminData);
  },

  /**
   * Updates the maximum auditor count for an account.
   * @param {string} id - The account ID.
   * @param {object} countData - The new max auditor count, e.g., { count: 10 }.
   * @returns {Promise}
   */
  updateMaxAuditorCount(id, countData) {
    return http.put(`/iam/tenant-api/v1/accounts/${id}/max-auditor-count`, countData);
  },

  getOrganizationRegistrationTypes() {
    const lang = localStorage.getItem('lang') || 'zhCN';
    const acceptLang = lang === 'zhCN' ? 'zh-CN' : lang;
    return http.get('/iam/tenant-api/v1/registration-types/organizations', {
      headers: {
        'Accept-Language': acceptLang
      }
    });
  },

  getInvoiceInfos(userId) {
    return http.get('/api/payment/account/invoice-info', { headers: { 'X-User-Id': userId } });
  },

  createInvoiceInfo(data, userId) {
    return http.post('/api/payment/account/invoice-info', data, { headers: { 'X-User-Id': userId } });
  },

  updateInvoiceInfo(id, data, userId) {
    return http.put(`/api/payment/account/invoice-info/${id}`, data, { headers: { 'X-User-Id': userId } });
  },

  deleteInvoiceInfo(id, userId) {
    return http.delete(`/api/payment/account/invoice-info/${id}`, { headers: { 'X-User-Id': userId } });
  },

  getInvoiceInfosForOrder(orderNo) {
    return http.get(`/api/payment/account/invoice-info/admin/orders/${encodeURIComponent(orderNo)}`);
  },

  setDefaultInvoiceInfo(id, userId) {
    return http.patch(`/api/payment/account/invoice-info/${id}/default`, null, { headers: { 'X-User-Id': userId } });
  }
};
